import { useEffect, useReducer, useRef } from "react"
import { useConnectionState } from "@rocicorp/zero/react"

import { Button, Dialog } from "@productlane/ui"

import {
	initialOfflineVisibility,
	OFFLINE_GRACE_MS,
	offlineVisibilityReducer,
	shouldShowOfflineModal,
} from "@/lib/offline-modal-visibility"
import { getReconnectDelay, RECONNECT_MAX_ATTEMPTS } from "@/lib/reconnect"
import { useZero } from "@/lib/zero"

export function OfflineModal() {
	const zero = useZero()
	const state = useConnectionState()
	const isPaused = state.name === "error"
	const connectionDown = isPaused || state.name === "disconnected"

	const [visibility, dispatch] = useReducer(
		offlineVisibilityReducer,
		initialOfflineVisibility,
	)
	const graceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	)

	const startGrace = () => {
		if (graceTimer.current) clearTimeout(graceTimer.current)
		graceTimer.current = setTimeout(
			() => dispatch({ type: "grace-elapsed" }),
			OFFLINE_GRACE_MS,
		)
	}

	// Feed connection changes into the grace state machine.
	useEffect(() => {
		dispatch({ type: "connection-changed", down: connectionDown })
		if (connectionDown) startGrace()
		else if (graceTimer.current) clearTimeout(graceTimer.current)
	}, [connectionDown])

	// A returning tab gets a fresh grace window (and a reconnect nudge) before the
	// modal is allowed to appear.
	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState !== "visible") return
			dispatch({ type: "tab-visible" })
			if (zero.connection.state.current.name === "error")
				void zero.connection.connect()
			if (
				zero.connection.state.current.name === "error" ||
				zero.connection.state.current.name === "disconnected"
			)
				startGrace()
		}
		document.addEventListener("visibilitychange", onVisible)
		return () => document.removeEventListener("visibilitychange", onVisible)
	}, [zero])

	const attemptRef = useRef(0)
	useEffect(() => {
		if (state.name === "connected") attemptRef.current = 0
	}, [state.name])

	// `error` never retries on its own; nudge it back with capped backoff.
	useEffect(() => {
		if (!isPaused) return
		let timer: ReturnType<typeof setTimeout> | undefined
		let cancelled = false

		const reconnect = () => {
			if (cancelled) return
			if (zero.connection.state.current.name !== "error") return
			void zero.connection.connect()
		}
		const schedule = () => {
			if (cancelled || attemptRef.current >= RECONNECT_MAX_ATTEMPTS) return
			const delay = getReconnectDelay(attemptRef.current)
			attemptRef.current += 1
			timer = setTimeout(() => {
				reconnect()
				schedule()
			}, delay)
		}

		schedule()
		window.addEventListener("online", reconnect)
		return () => {
			cancelled = true
			if (timer) clearTimeout(timer)
			window.removeEventListener("online", reconnect)
		}
	}, [isPaused, zero])

	const isOffline = shouldShowOfflineModal(visibility)

	const browserOffline =
		typeof navigator !== "undefined" && navigator.onLine === false

	const title = browserOffline ? "You're offline" : "Can't reach Productlane"
	const description = browserOffline
		? "Check your connection and try refreshing."
		: "A browser extension or network proxy may be blocking the connection. Try an incognito window or disabling extensions, then reload."

	return (
		<Dialog
			isOpen={isOffline}
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			onOpenChange={() => {}}
			title={title}
			description={description}
			showCloseButton={false}
			size="sm"
		>
			<div className="flex justify-end gap-2">
				{isPaused ? (
					<Button
						variant="outline"
						onClick={() => void zero.connection.connect()}
					>
						Retry
					</Button>
				) : null}
				<Button variant="outline" onClick={() => window.location.reload()}>
					Reload
				</Button>
			</div>
		</Dialog>
	)
}
